'use client'

import { useState, useEffect, useRef } from 'react'
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { AlertCircle, FileText, Timer, ArrowUpDown, Image, Video, X, Search, Sparkles } from "lucide-react"
import { ContentTypeMetadata, getOptionLabel, getOptionValue, getOptionIcon } from '@/lib/content/contentTypes'
import { supabase } from '@/lib/supabase'
import { Database } from '@/../database.types'
import { useBrands } from '@/lib/BrandsProvider'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { cn } from '@/lib/utils'
import { Slider } from '@/components/ui/clicky-slider'
import { Badge } from "@/components/ui/badge"
import { getPlatformIcon } from '@/lib/platform-utils'
import { VisuallyHidden } from '../ui/visually-hidden'
import { getDefaultPlatformKey } from '@/lib/content/platform-mappings'
import { POST_TYPE_LABELS, type PlatformPostType } from '@/types/content.types'

type Campaign = Database['public']['Tables']['campaigns']['Row']
type ContentDependency = {
    id: string
    name: string
    content_type: string
    placeholder?: string
}

// Define interfaces for metadata structure
interface Ideas {
    unique_insights?: string;
    brand_opinion?: string;
    hot_takes?: string;
    controversial_takes?: string;
}

interface ConvergenceMetadata {
    ideas?: Ideas;
}

interface Convergence {
    id: string;
    name: string;
    trend: string;
    metadata?: ConvergenceMetadata;
    [key: string]: any; // Allow other properties
}

// Define SelectOption type locally since it's not exported from contentTypes
interface SelectOption {
    label: string
    value: string
    status?: 'available' | 'coming_soon'
}

// Extended ContentTypeMetadata interface to include new properties
interface ExtendedContentTypeMetadata extends Omit<ContentTypeMetadata, 'requiredInputs'> {
    requiredInputs: {
        name: string
        description: string
        type: 'text' | 'url' | 'select' | 'select_buttons' | 'multiselect' | 'number' | 'ideas' | 'select_slider'
        required: boolean
        options?: string[] | SelectOption[]
        multipleSelections?: boolean
        placeholder?: string
        default?: any
        suggestions?: string[]
        showWhen?: {
            field: string
            values: string[]
        }
    }[]
}

interface ContentSetupFlowProps {
    contentType: ExtendedContentTypeMetadata
    isOpen: boolean
    onClose: () => void
    onComplete: (data: {
        campaignId?: string,
        convergenceId?: string,
        inputs: Record<string, any>,
        dependencies: Record<string, string>,
        platform_key?: string
    }) => void
    defaultCampaignId?: string | null
    defaultConvergenceId?: string | null
}

// Define loading messages for different operations
const LOADING_MESSAGES = {
  campaigns: [
    "Loading available campaigns...",
    "Analyzing campaign data...",
    "Preparing campaign options...",
    "Finalizing campaign selection..."
  ],
  dependencies: [
    "Loading content dependencies...",
    "Analyzing content relationships...",
    "Preparing dependency options...",
    "Finalizing dependency selection..."
  ]
}

// Helper function to check if an option is a SelectOption
// This function is now imported from '@/lib/content/contentTypes'

// Helper function to get option label
// This function is now imported from '@/lib/content/contentTypes'

// Helper function to get option value
// This function is now imported from '@/lib/content/contentTypes'

// Helper function to safely access metadata
const parseConvergenceMetadata = (rawMetadata: any): ConvergenceMetadata => {
    if (!rawMetadata) return {};
    
    try {
        // If metadata is already a string, parse it
        if (typeof rawMetadata === 'string') {
            return JSON.parse(rawMetadata) as ConvergenceMetadata;
        }
        
        // If it's already an object, cast it
        return rawMetadata as ConvergenceMetadata;
    } catch (error) {
        console.error('Error parsing convergence metadata:', error);
        return {};
    }
};

// Helper function to check if a field should be shown based on conditional logic
const shouldShowField = (input: ContentTypeMetadata['requiredInputs'][0], inputValues: Record<string, any>): boolean => {
    if (!input.showWhen) return true;

    const { field, values } = input.showWhen;
    const currentValue = inputValues[field];

    // For single values, check direct match
    if (typeof currentValue === 'string') {
        return values.includes(currentValue);
    }

    // For arrays (multiselect), check if any selected value matches
    if (Array.isArray(currentValue)) {
        return currentValue.some(value => values.includes(value));
    }

    return false;
};

// Helper function to check if an option should be shown based on conditional logic
const shouldShowOption = (
    option: any,
    inputValues: Record<string, any>
): boolean => {
    // If option doesn't have showWhen, always show it
    if (!option.showWhen) return true;

    const { field, values } = option.showWhen;
    const currentValue = inputValues[field];

    // For single values, check direct match
    if (typeof currentValue === 'string') {
        return values.includes(currentValue);
    }

    // For arrays (multiselect), check if any selected value matches
    if (Array.isArray(currentValue)) {
        return currentValue.some(value => values.includes(value));
    }

    return false;
};

// Helper function to check if an option is coming soon
const isOptionComingSoon = (option: any): boolean => {
    if (typeof option === 'object' && option.status) {
        return option.status === 'coming_soon';
    }
    return false;
};

export default function ContentSetupFlow({
    contentType,
    isOpen,
    onClose,
    onComplete,
    defaultCampaignId,
    defaultConvergenceId
}: ContentSetupFlowProps) {
    const { selectedBrand } = useBrands()
    const [campaigns, setCampaigns] = useState<Campaign[]>([])
    const [selectedCampaign, setSelectedCampaign] = useState<string>('')
    const [isLoadingCampaigns, setIsLoadingCampaigns] = useState(true)
    const [campaignError, setCampaignError] = useState<string | null>(null)

    // Convergence selection state
    const [convergences, setConvergences] = useState<Convergence[]>([])
    const [selectedConvergence, setSelectedConvergence] = useState<string>('')
    const [convergenceSearchTerm, setConvergenceSearchTerm] = useState<string>('')
    const [isLoadingConvergences, setIsLoadingConvergences] = useState(false)
    const [showConvergenceDropdown, setShowConvergenceDropdown] = useState(false)
    const [focusedConvergenceIndex, setFocusedConvergenceIndex] = useState<number>(-1)
    const convergenceInputRef = useRef<HTMLInputElement>(null)
    const convergenceDropdownRef = useRef<HTMLDivElement>(null)
    
    // Reset convergence dropdown state when dialog opens/closes
    useEffect(() => {
        if (!isOpen) {
            setShowConvergenceDropdown(false)
            setFocusedConvergenceIndex(-1)
            setConvergenceSearchTerm('')
        }
    }, [isOpen])

    // Input collection state
    const [inputValues, setInputValues] = useState<Record<string, any>>(() => {
        // Initialize with default values if provided
        const initialValues: Record<string, any> = {};
        contentType.requiredInputs.forEach(input => {
            if (input.default !== undefined) {
                initialValues[input.name] = input.default;
            }
        });
        
        // Add default media generation value for blog_post and social_post
        if (contentType.id === 'blog_post' || contentType.id === 'social_post') {
            initialValues.mediaGeneration = 'none'; // Default to no media generation
        }
        
        return initialValues;
    });
    const [inputErrors, setInputErrors] = useState<Record<string, string>>({})
    
    // Dependencies state
    const [availableDependencies, setAvailableDependencies] = useState<Record<string, ContentDependency[]>>({})
    const [selectedDependencies, setSelectedDependencies] = useState<Record<string, string>>({})
    const [isLoadingDependencies, setIsLoadingDependencies] = useState(false)
    const [dependencyError, setDependencyError] = useState<string | null>(null)
    
    // Convergence state for ideas
    const [convergenceData, setConvergenceData] = useState<Convergence | null>(null)
    
    // Message rotation state
    const [shownMessages, setShownMessages] = useState<string[]>([])
    
    // Feature visibility based on content type requirements
    const hasRequiredInputs = contentType.requiredInputs.some(input => input.required)
    const hasDependencies = contentType.dependencies.length > 0
    
    // Active suggestion state
    const [activeSuggestion, setActiveSuggestion] = useState<string | null>(null)
    
    // Function to show progress messages with rotation
    const showProgressMessages = (messages: string[]) => {
        setShownMessages([])
        let messageIndex = 0
        const messageInterval = setInterval(() => {
            if (messageIndex < messages.length) {
                setShownMessages(prev => [...prev, messages[messageIndex]])
                messageIndex++
            }
        }, 1500)
        return messageInterval
    }
    
    // Load campaigns on component mount
    useEffect(() => {
        async function loadCampaigns() {
            if (!selectedBrand) return
            
            setIsLoadingCampaigns(true)
            setCampaignError(null)
            
            let messageInterval: ReturnType<typeof setInterval> | undefined
            
            try {
                messageInterval = showProgressMessages(LOADING_MESSAGES.campaigns)
                
                const { data: allCampaigns, error } = await supabase
                    .from('campaigns')
                    .select('*')
                    .eq('brand_id', selectedBrand.id)
                    .eq('status', 'Active')
                    .order('name')

                if (error) throw error

                if (allCampaigns && allCampaigns.length > 0) {
                    setCampaigns(allCampaigns)
                    
                    // Use defaultCampaignId if provided, otherwise default to base campaign
                    if (defaultCampaignId) {
                        setSelectedCampaign(defaultCampaignId)
                    } else {
                        // Default to base campaign if available
                        const baseCampaign = allCampaigns.find(c => c.campaign_type === 'BASE')
                        if (baseCampaign) {
                            setSelectedCampaign(baseCampaign.id)
                        }
                    }
                } else {
                    setCampaignError('No active campaigns found for this brand')
                }
            } catch (error) {
                console.error('Error loading campaigns:', error)
                setCampaignError('Failed to load campaigns')
            } finally {
                if (messageInterval) clearInterval(messageInterval)
                setIsLoadingCampaigns(false)
                setShownMessages([])
            }
        }

        if (isOpen) {
            loadCampaigns()
        }
    }, [isOpen, selectedBrand, defaultCampaignId])

    // Load convergences on component mount
    useEffect(() => {
        async function loadConvergences() {
            if (!selectedBrand) return

            setIsLoadingConvergences(true)

            try {
                const { data: convergencesData, error } = await supabase
                    .from('convergences')
                    .select('id, name, trend, convergence, status, type, metadata')
                    .eq('brand_id', selectedBrand.id)
                    .in('status', ['Unused', 'Used'])
                    .order('created_at', { ascending: false })
                    .limit(100)

                if (error) throw error

                if (convergencesData) {
                    setConvergences(convergencesData as Convergence[])
                }
            } catch (error) {
                console.error('Error loading convergences:', error)
            } finally {
                setIsLoadingConvergences(false)
            }
        }

        if (isOpen) {
            loadConvergences()
        }
    }, [isOpen, selectedBrand])

    // Pre-select convergence when defaultConvergenceId is provided
    useEffect(() => {
        if (defaultConvergenceId && convergences.length > 0) {
            const convergence = convergences.find(c => c.id === defaultConvergenceId)
            if (convergence) {
                setSelectedConvergence(defaultConvergenceId)
                setConvergenceSearchTerm(convergence.name)
            }
        }
    }, [defaultConvergenceId, convergences])

    // Close convergence dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (convergenceInputRef.current &&
                convergenceDropdownRef.current &&
                !convergenceInputRef.current.contains(event.target as Node) &&
                !convergenceDropdownRef.current.contains(event.target as Node)) {
                setShowConvergenceDropdown(false)
                setFocusedConvergenceIndex(-1)
            }
        }

        if (showConvergenceDropdown) {
            document.addEventListener('mousedown', handleClickOutside)
            return () => document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [showConvergenceDropdown])

    // Helper function to get filtered convergences
    const getFilteredConvergences = () => {
        const searchLower = convergenceSearchTerm.toLowerCase()
        return convergences.filter(conv =>
            conv.name.toLowerCase().includes(searchLower) ||
            conv.trend.toLowerCase().includes(searchLower) ||
            (conv.convergence && conv.convergence.toLowerCase().includes(searchLower))
        )
    }

    // Handle keyboard navigation in convergence dropdown
    const handleConvergenceKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!showConvergenceDropdown) return

        const filteredConvergences = getFilteredConvergences()

        if (e.key === 'ArrowDown') {
            e.preventDefault()
            setFocusedConvergenceIndex(prev =>
                prev < filteredConvergences.length - 1 ? prev + 1 : prev
            )
        } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            setFocusedConvergenceIndex(prev => prev > 0 ? prev - 1 : -1)
        } else if (e.key === 'Enter') {
            e.preventDefault()
            if (focusedConvergenceIndex >= 0 && focusedConvergenceIndex < filteredConvergences.length) {
                const convergence = filteredConvergences[focusedConvergenceIndex]
                setSelectedConvergence(convergence.id)
                setConvergenceSearchTerm(convergence.name)
                setShowConvergenceDropdown(false)
                setFocusedConvergenceIndex(-1)
            }
        } else if (e.key === 'Escape') {
            e.preventDefault()
            setShowConvergenceDropdown(false)
            setFocusedConvergenceIndex(-1)
        }
    }

    // Reset focused index when search term changes
    useEffect(() => {
        setFocusedConvergenceIndex(-1)
    }, [convergenceSearchTerm])

    // Scroll focused item into view
    useEffect(() => {
        if (focusedConvergenceIndex >= 0 && convergenceDropdownRef.current) {
            const focusedElement = convergenceDropdownRef.current.children[focusedConvergenceIndex] as HTMLElement
            if (focusedElement) {
                focusedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
            }
        }
    }, [focusedConvergenceIndex])

    // Load dependencies when campaign changes
    useEffect(() => {
        async function loadDependencies() {
            if (!selectedCampaign || !contentType.dependencies.length) return
            
            setIsLoadingDependencies(true)
            setDependencyError(null)
            
            let messageInterval: ReturnType<typeof setInterval> | undefined
            
            try {
                messageInterval = showProgressMessages(LOADING_MESSAGES.dependencies)
                
                const contentTypes = contentType.dependencies.map(d => d.type) as ("social_content" | "social_post" | "ad_group" | "blog_post" | "landing_page" | "email_sales_sequence" | "email_educational_sequence" | "email_newsletter" | "quiz" | "video" | "tool")[]
                const { data: content, error } = await supabase
                    .from('contents')
                    .select('id, name, content_type')
                    .eq('campaign_id', selectedCampaign)
                    .eq('status', 'READY')
                    .in('content_type', contentTypes)

                if (error) throw error
                
                // Group content by type
                const groupedContent = (content || []).reduce((acc, item) => {
                    if (!acc[item.content_type]) {
                        acc[item.content_type] = []
                    }
                    acc[item.content_type].push(item)
                    return acc
                }, {} as Record<string, ContentDependency[]>)
                
                setAvailableDependencies(groupedContent)
            } catch (err) {
                setDependencyError('Failed to load available dependencies')
                console.error('Error loading dependencies:', err)
            } finally {
                if (messageInterval) clearInterval(messageInterval)
                setIsLoadingDependencies(false)
                setShownMessages([])
            }
        }
        
        async function loadConvergenceData() {
            if (!selectedConvergence) {
                // Clear convergence data if no convergence is selected
                setConvergenceData(null);
                return;
            }

            try {
                console.log('Attempting to load convergence data for convergence:', selectedConvergence);
                
                // Fetch the convergence data directly
                const { data: convergenceData, error: convergenceError } = await supabase
                    .from('convergences')
                    .select('*')
                    .eq('id', selectedConvergence)
                    .single()
                
                if (convergenceError) {
                    console.error('Error fetching convergence data:', convergenceError);
                    setConvergenceData(null);
                    return;
                }
                
                console.log('Convergence data:', convergenceData);
                
                // Parse the metadata safely
                const parsedMetadata = parseConvergenceMetadata(convergenceData.metadata);
                console.log('Parsed metadata:', parsedMetadata);
                
                const typedConvergence: Convergence = {
                    ...convergenceData,
                    metadata: parsedMetadata
                };
                
                setConvergenceData(typedConvergence);
                console.log('Set convergence data successfully');
            } catch (err) {
                console.error('Error in loadConvergenceData:', err);
                setConvergenceData(null);
            }
        }
        
        if (selectedCampaign) {
            loadDependencies();
        }
        
        // Check if we need to fetch convergence data for ideas field
        const hasIdeasInput = contentType.requiredInputs.some(input => input.type === 'ideas');
        if (hasIdeasInput) {
            loadConvergenceData();
        }
    }, [selectedCampaign, selectedConvergence, contentType.dependencies, contentType.requiredInputs])
    
    // Input handlers
    const handleInputChange = (name: string, value: any) => {
        setInputValues(prev => {
            const newValues = {
                ...prev,
                [name]: value
            };
            
            // Clear values for fields that become hidden due to conditional logic
            contentType.requiredInputs.forEach(input => {
                if (input.showWhen && !shouldShowField(input, newValues)) {
                    delete newValues[input.name];
                }
            });
            
            return newValues;
        });
        
        // Clear error if value is set
        if (value) {
            setInputErrors(prev => {
                const newErrors = { ...prev }
                delete newErrors[name]
                return newErrors
            })
        }
    }
    
    // Handle select button click
    const handleSelectButtonClick = (inputName: string, option: string | SelectOption, multipleSelections?: boolean) => {
        const optionValue = getOptionValue(option);
        
        if (multipleSelections) {
            // For multiple selections, toggle the value in an array
            const currentValues = Array.isArray(inputValues[inputName]) 
                ? [...inputValues[inputName]] 
                : [];
            
            if (currentValues.includes(optionValue)) {
                handleInputChange(inputName, currentValues.filter(v => v !== optionValue));
            } else {
                handleInputChange(inputName, [...currentValues, optionValue]);
            }
        } else {
            // For single selection, just set the value
            handleInputChange(inputName, optionValue);
        }
    }
    
    // Check if an option is selected
    const isOptionSelected = (inputName: string, option: string | SelectOption, multipleSelections?: boolean): boolean => {
        const optionValue = getOptionValue(option);
        
        if (multipleSelections) {
            return Array.isArray(inputValues[inputName]) && inputValues[inputName]?.includes(optionValue);
        } else {
            return inputValues[inputName] === optionValue;
        }
    }
    
    // Dependency handlers
    const handleDependencySelect = (type: string, value: string) => {
        setSelectedDependencies(prev => ({
            ...prev,
            [type]: value
        }))
    }
    
    // Final completion
    const handleComplete = () => {
        // Campaign selection is now optional - removed validation

        // Validate required inputs
        const newErrors: Record<string, string> = {}
        let isValid = true
        
        contentType.requiredInputs
            .filter(input => shouldShowField(input, inputValues))
            .forEach(input => {
                if (input.required && (inputValues[input.name] === undefined || inputValues[input.name] === '')) {
                    newErrors[input.name] = 'This field is required'
                    isValid = false
                }
            })
        
        if (!isValid) {
            setInputErrors(newErrors)
            return
        }
        
        // Validate required dependencies
        if (hasDependencies) {
            const missingRequiredDeps = contentType.dependencies
                .filter(dep => dep.required)
                .some(dep => !selectedDependencies[dep.type])
                
            if (missingRequiredDeps) {
                return
            }
        }
        
        // Get default platform key for content types that require it
        const platformKey = getDefaultPlatformKey(contentType.id)

        onComplete({
            campaignId: selectedCampaign || undefined,
            convergenceId: selectedConvergence || undefined,
            inputs: inputValues,
            dependencies: selectedDependencies,
            platform_key: platformKey || undefined
        })
    }
    
    // Validation
    const isCampaignStepValid = true  // Campaign is now optional
    
    const isInputStepValid = () => {
        return contentType.requiredInputs
            .filter(input => shouldShowField(input, inputValues))
            .filter(input => input.required)
            .every(input => !!inputValues[input.name])
    }
    
    const isDependencyStepValid = () => {
        return contentType.dependencies
            .filter(dep => dep.required)
            .every(dep => !!selectedDependencies[dep.type])
    }
    
    const getMissingDependencyTypes = () => {
        return contentType.dependencies
            .filter(dep => dep.required && !availableDependencies[dep.type]?.length)
            .map(dep => dep.type)
    }
    
    const missingDependencyTypes = getMissingDependencyTypes()
    
    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <VisuallyHidden><DialogTitle>Create Your New {contentType.title}</DialogTitle></VisuallyHidden>
            <DialogContent className="max-w-4xl overflow-y-auto max-h-[95vh] p-0">
                <>
                {/* Enhanced Dialog Header with Gradient Background */}
                <div className="relative overflow-hidden bg-gradient-to-br from-slate-50 via-white to-blue-50/30 border-b border-blue-100/50 p-8">
                    <div className="absolute inset-0 bg-grid-pattern opacity-5" />
                    <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-200/20 to-transparent rounded-full -translate-y-16 translate-x-16" />
                    <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-indigo-200/15 to-transparent rounded-full translate-y-12 -translate-x-12" />
                    <div className="relative">
                        <div className="flex items-center gap-4 mb-4">
                            <div className="p-3 bg-white/60 backdrop-blur-sm rounded-2xl shadow-sm border border-white/20">
                                <FileText className="h-6 w-6 text-blue-600" />
                            </div>
                            <div>
                                <h2 className="text-3xl font-bold bg-gradient-to-r from-blue-700 via-indigo-700 to-purple-700 bg-clip-text text-transparent mb-2">
                                    Create Your New {contentType.title}
                                </h2>
                                <p className="text-gray-600 leading-relaxed">
                                    Fill out the details below to craft your {contentType.title.toLowerCase()}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div className="overflow-y-auto px-8 py-6 bg-gradient-to-br from-white via-slate-50/30 to-blue-50/20">
                    {/* Campaign Selection Section - HIDDEN: Campaigns removed from user flow */}
                    {false && (
                    <div className="relative mb-8">
                        <div className="bg-white/60 backdrop-blur-sm rounded-2xl border border-blue-100/50 shadow-sm p-6 transition-all duration-300 hover:shadow-md">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
                                    <Timer className="h-4 w-4 text-white" />
                                </div>
                                <h3 className="text-xl font-semibold bg-gradient-to-r from-blue-700 to-indigo-700 bg-clip-text text-transparent">
                                    Campaign Selection
                                </h3>
                            </div>
                        {isLoadingCampaigns ? (
                            <div className="flex flex-col items-center p-4 space-y-4">
                                <div className="w-6 h-6 border-2 border-green-600 border-t-transparent rounded-full animate-spin" />
                                {shownMessages.map((message, index) => (
                                    <div
                                        key={index}
                                        className="text-sm text-muted-foreground animate-fade-in"
                                    >
                                        {message}
                                    </div>
                                ))}
                            </div>
                        ) : campaignError ? (
                            <Alert variant="destructive">
                                <AlertCircle className="h-4 w-4" />
                                <AlertDescription>{campaignError}</AlertDescription>
                            </Alert>
                        ) : (
                            <div className="space-y-2">
                                <Label htmlFor="campaign">Campaign</Label>
                                <Select
                                    value={selectedCampaign}
                                    onValueChange={setSelectedCampaign}
                                >
                                    <SelectTrigger className="w-full">
                                        <SelectValue placeholder="Select campaign..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {campaigns.map((campaign) => (
                                            <SelectItem
                                                key={campaign.id}
                                                value={campaign.id}
                                                className={campaign.campaign_type === 'BASE' ? 'font-medium' : ''}
                                            >
                                                {campaign.name}
                                                {campaign.campaign_type === 'BASE' && ' (Brand Campaign)'}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                        </div>
                    </div>
                    )}

                    {/* Convergence Selection Section - Optional type-ahead search */}
                    <div className="relative mb-8 z-10">
                        <div className="bg-white/60 backdrop-blur-sm rounded-2xl border border-purple-100/50 shadow-sm p-6 transition-all duration-300 hover:shadow-md">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-violet-600 rounded-xl flex items-center justify-center shadow-lg">
                                    <Sparkles className="h-4 w-4 text-white" />
                                </div>
                                <h3 className="text-xl font-semibold bg-gradient-to-r from-purple-700 to-violet-700 bg-clip-text text-transparent">
                                    Convergence (Optional)
                                </h3>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="convergence" className="text-sm text-gray-600">
                                    Select a trend convergence to base this content on
                                </Label>
                                <div className="relative">
                                    <div className="relative">
                                        <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                                        <Input
                                            ref={convergenceInputRef}
                                            placeholder="Search convergences by name or trend..."
                                            value={convergenceSearchTerm}
                                            onChange={(e) => {
                                                setConvergenceSearchTerm(e.target.value)
                                                // Only open dropdown if user is actively typing and there are convergences
                                                if (convergences.length > 0) {
                                                    setShowConvergenceDropdown(true)
                                                }
                                            }}
                                            onFocus={() => {
                                                // Only open dropdown on focus if there are convergences to show
                                                if (convergences.length > 0) {
                                                    setShowConvergenceDropdown(true)
                                                }
                                            }}
                                            onKeyDown={handleConvergenceKeyDown}
                                            className="pl-10"
                                            autoComplete="off"
                                        />
                                    </div>

                                    {/* Dropdown list */}
                                    {showConvergenceDropdown && convergences.length > 0 && (() => {
                                        const filteredConvergences = getFilteredConvergences()
                                        return filteredConvergences.length > 0 ? (
                                            <div
                                                ref={convergenceDropdownRef}
                                                className="absolute z-[100] w-full mt-2 bg-white border-2 border-purple-200 rounded-xl shadow-2xl max-h-80 overflow-y-auto"
                                            >
                                                {filteredConvergences.map((convergence, index) => (
                                                    <div
                                                        key={convergence.id}
                                                        className={cn(
                                                            "p-4 cursor-pointer border-b border-gray-100 last:border-b-0 transition-all duration-150",
                                                            selectedConvergence === convergence.id && "bg-purple-100 border-purple-200",
                                                            index === focusedConvergenceIndex
                                                                ? "bg-purple-100 ring-2 ring-inset ring-purple-400 shadow-inner"
                                                                : "hover:bg-purple-50"
                                                        )}
                                                        onClick={() => {
                                                            setSelectedConvergence(convergence.id)
                                                            setConvergenceSearchTerm(convergence.name)
                                                            setShowConvergenceDropdown(false)
                                                            setFocusedConvergenceIndex(-1)
                                                        }}
                                                        onMouseEnter={() => setFocusedConvergenceIndex(index)}
                                                    >
                                                        <div className="font-semibold text-gray-900 text-base leading-tight mb-2">
                                                            {convergence.name}
                                                        </div>
                                                        <div className="text-sm text-gray-700 mb-2">
                                                            <span className="font-semibold text-purple-700">Trend:</span>{' '}
                                                            <span className="text-gray-800">{convergence.trend}</span>
                                                        </div>
                                                        {convergence.convergence && (
                                                            <div className="text-xs text-gray-600 leading-relaxed line-clamp-3">
                                                                {convergence.convergence}
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="absolute z-[100] w-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg p-3">
                                                <div className="text-sm text-gray-500 text-center">No convergences match your search</div>
                                            </div>
                                        )
                                    })()}

                                    {/* Show selected convergence info */}
                                    {selectedConvergence && (
                                        <div className="mt-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
                                            <div className="flex items-center justify-between">
                                                <div className="flex-1">
                                                    <div className="font-medium text-purple-900">
                                                        {convergences.find(c => c.id === selectedConvergence)?.name}
                                                    </div>
                                                    <div className="text-sm text-purple-700 mt-1">
                                                        {convergences.find(c => c.id === selectedConvergence)?.trend}
                                                    </div>
                                                </div>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => {
                                                        setSelectedConvergence('')
                                                        setConvergenceSearchTerm('')
                                                    }}
                                                >
                                                    <X className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </div>
                                    )}

                                    {isLoadingConvergences && (
                                        <div className="text-sm text-gray-500 mt-2">Loading convergences...</div>
                                    )}

                                    {!isLoadingConvergences && convergences.length === 0 && (
                                        <div className="text-sm text-gray-500 mt-2">No convergences available</div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Separator */}
                    {/* {(hasRequiredInputs || hasDependencies) && <Separator className="my-4" />} */}

                    {/* Configuration Section */}
                    {contentType.requiredInputs && contentType.requiredInputs.length > 0 && (
                        <div className="relative mb-8">
                            <div className="bg-white/60 backdrop-blur-sm rounded-2xl border border-green-100/50 shadow-sm p-6 transition-all duration-300 hover:shadow-md">
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg">
                                        <FileText className="h-4 w-4 text-white" />
                                    </div>
                                    <h3 className="text-xl font-semibold bg-gradient-to-r from-green-700 to-emerald-700 bg-clip-text text-transparent">
                                        Content Configuration
                                    </h3>
                                </div>
                                
                                <div className="space-y-6">
                                    {contentType.requiredInputs
                                        .filter(input => shouldShowField(input, inputValues))
                                        .filter(input => input.name !== 'mediaGeneration') // Hide the simpler mediaGeneration input, use the styled section below instead
                                        .map(input => (
                                        <div key={input.name} className={cn(
                                            "space-y-3 p-5 rounded-xl transition-all duration-300 hover:shadow-sm border-2",
                                            input.required 
                                                ? "bg-gradient-to-r from-blue-50/80 via-white/60 to-indigo-50/40 border-blue-200/60 shadow-sm" 
                                                : "bg-gradient-to-r from-gray-50/50 via-white/40 to-slate-50/30 border-gray-200/50"
                                        )}>
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-3">
                                                    <Label htmlFor={input.name} className="text-base font-semibold text-gray-800">
                                                        {input.description}
                                                    </Label>
                                                    {input.required && (
                                                        <Badge variant="secondary" className="text-xs px-2 py-1 h-6 bg-gradient-to-r from-blue-100 to-indigo-100 hover:from-blue-200 hover:to-indigo-200 text-blue-700 border border-blue-200/50 transition-all duration-200">
                                                            Required
                                                        </Badge>
                                                    )}
                                                </div>
                                            </div>
                                        
                                            {input.type === 'text' && (
                                                <div className="space-y-3">
                                                    <div className={cn(
                                                        "rounded-xl border-2 bg-white/80 backdrop-blur-sm transition-all duration-200 focus-within:shadow-md",
                                                        input.required 
                                                            ? "border-blue-300/60 focus-within:border-blue-400 shadow-blue-100/20" 
                                                            : "border-gray-300/60 focus-within:border-gray-400"
                                                    )}>
                                                        <Input
                                                            id={input.name}
                                                            value={inputValues[input.name] || ''}
                                                            placeholder={input.placeholder}
                                                            onChange={e => handleInputChange(input.name, e.target.value)}
                                                            className={cn(
                                                                "border-0 focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent h-12 text-base",
                                                                inputErrors[input.name] ? 'text-red-600' : ''
                                                            )}
                                                        />
                                                        {input.suggestions && input.suggestions.length > 0 && (
                                                            <div className="border-t border-blue-100/50 p-4 bg-gradient-to-r from-blue-50/50 to-indigo-50/30">
                                                                <p className="text-sm font-medium text-blue-700 mb-3">Suggestions:</p>
                                                                <div className="flex flex-wrap gap-2">
                                                                    {input.suggestions.map((suggestion) => (
                                                                        <Button
                                                                            key={suggestion}
                                                                            type="button"
                                                                            variant={inputValues[input.name] === suggestion ? 'default' : 'ghost'}
                                                                            size="sm"
                                                                            onClick={() => handleInputChange(input.name, suggestion)}
                                                                            className={cn(
                                                                                "text-sm h-8 px-3 transition-all duration-300 rounded-lg transform hover:scale-105",
                                                                                "hover:bg-blue-100 hover:text-blue-700 active:scale-95",
                                                                                inputValues[input.name] === suggestion 
                                                                                    ? "bg-gradient-to-r from-blue-500 to-indigo-600 text-white hover:from-blue-600 hover:to-indigo-700 shadow-lg" 
                                                                                    : "bg-white/80 border border-blue-200 hover:border-blue-300",
                                                                                "cursor-pointer"
                                                                            )}
                                                                        >
                                                                            <span className="mr-1 text-xs opacity-70">+</span>
                                                                            {suggestion}
                                                                        </Button>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}
                                                </div>
                                                {inputErrors[input.name] && (
                                                    <p className="text-sm text-red-500">{inputErrors[input.name]}</p>
                                                )}
                                            </div>
                                        )}
                                        
                                            {input.type === 'number' && (
                                                <div className={cn(
                                                    "rounded-xl border-2 bg-white/80 backdrop-blur-sm transition-all duration-200 focus-within:shadow-md",
                                                    input.required 
                                                        ? "border-blue-300/60 focus-within:border-blue-400 shadow-blue-100/20" 
                                                        : "border-gray-300/60 focus-within:border-gray-400"
                                                )}>
                                                    <Input
                                                        id={input.name}
                                                        type="number"
                                                        value={inputValues[input.name] || ''}
                                                        placeholder={input.placeholder}
                                                        onChange={e => handleInputChange(input.name, parseFloat(e.target.value))}
                                                        className={cn(
                                                            "border-0 focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent h-12 text-base",
                                                            inputErrors[input.name] ? 'text-red-600' : ''
                                                        )}
                                                    />
                                                </div>
                                            )}
                                        
                                            {input.type === 'select' && input.options && (
                                                <Select
                                                    value={inputValues[input.name] || ''}
                                                    onValueChange={value => handleInputChange(input.name, value)}
                                                >
                                                    <SelectTrigger className={cn(
                                                        "w-full bg-white/80 backdrop-blur-sm border-2 transition-all duration-200 rounded-xl h-12",
                                                        inputErrors[input.name] ? 'border-red-300 focus:border-red-400' : '',
                                                        input.required 
                                                            ? "border-blue-300/60 hover:border-blue-300 focus:border-blue-400" 
                                                            : "border-gray-300/60 hover:border-gray-300 focus:border-gray-400"
                                                    )}>
                                                        <SelectValue placeholder={input.placeholder || `Select ${input.description.toLowerCase()}`} />
                                                    </SelectTrigger>
                                                    <SelectContent className="bg-white/95 backdrop-blur-sm border-blue-200 rounded-xl">
                                                        {input.options.map(option => (
                                                            <SelectItem 
                                                                key={getOptionValue(option)} 
                                                                value={getOptionValue(option)}
                                                                className="transition-all duration-200 rounded-lg mx-1 my-0.5 hover:bg-blue-50"
                                                            >
                                                                {getOptionLabel(option)}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            )}
                                        
                                            {input.type === 'select_buttons' && input.options && (
                                                <>
                                                    <div className="flex flex-wrap gap-3">
                                                        {input.options
                                                            .filter((option: any) => shouldShowOption(option, inputValues))
                                                            .map(option => {
                                                                const selected = isOptionSelected(
                                                                    input.name,
                                                                    option,
                                                                    input.multipleSelections
                                                                );
                                                                const optionIcon = getOptionIcon(option);
                                                                const comingSoon = isOptionComingSoon(option);

                                                                return (
                                                                    <div key={getOptionValue(option)} className="relative">
                                                                        <Button
                                                                            type="button"
                                                                            variant={selected ? "default" : "outline"}
                                                                            onClick={() => {
                                                                                if (!comingSoon) {
                                                                                    handleSelectButtonClick(
                                                                                        input.name,
                                                                                        option,
                                                                                        input.multipleSelections
                                                                                    );
                                                                                }
                                                                            }}
                                                                            disabled={comingSoon}
                                                                            className={cn(
                                                                                "flex items-center gap-2 h-11 px-4 transition-all duration-300 rounded-xl border-2",
                                                                                !comingSoon && "transform hover:scale-105 active:scale-95",
                                                                                selected && !comingSoon
                                                                                    ? "bg-gradient-to-r from-blue-500 to-indigo-600 text-white border-blue-500 hover:from-blue-600 hover:to-indigo-700 shadow-lg"
                                                                                    : !comingSoon
                                                                                    ? "bg-white/80 backdrop-blur-sm border-blue-200 hover:border-blue-300 hover:bg-blue-50 text-gray-700"
                                                                                    : "bg-gray-100/60 backdrop-blur-sm border-gray-300 text-gray-400 cursor-not-allowed opacity-60"
                                                                            )}
                                                                        >
                                                                            {optionIcon && getPlatformIcon(optionIcon, "h-4 w-4")}
                                                                            {getOptionLabel(option)}
                                                                            {comingSoon && (
                                                                                <Badge variant="secondary" className="ml-2 text-xs bg-orange-100 text-orange-700 border-orange-300">
                                                                                    Coming Soon
                                                                                </Badge>
                                                                            )}
                                                                        </Button>
                                                                    </div>
                                                                );
                                                            })}
                                                    </div>

                                                    {/* Show "when to use" guidance for platform_post_type when a type is selected */}
                                                    {input.name === 'platform_post_type' && inputValues.platform_post_type && (
                                                        <div className="mt-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl">
                                                            <p className="text-sm text-blue-900">
                                                                <span className="font-semibold">💡 When to use:</span>{' '}
                                                                {POST_TYPE_LABELS[inputValues.platform_post_type as PlatformPostType]?.whenToUse ||
                                                                 'Select a post type to see guidance'}
                                                            </p>
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        
                                            {input.type === 'multiselect' && input.options && (
                                                <div className="flex flex-wrap gap-3">
                                                    {input.options.map(option => {
                                                        const optionValue = getOptionValue(option);
                                                        const optionLabel = getOptionLabel(option);
                                                        const optionIcon = getOptionIcon(option);
                                                        const selected = Array.isArray(inputValues[input.name]) && 
                                                                        inputValues[input.name]?.includes(optionValue);
                                                        
                                                        return (
                                                            <Button
                                                                key={optionValue}
                                                                type="button"
                                                                variant={selected ? "default" : "outline"}
                                                                onClick={() => {
                                                                    const currentValues = Array.isArray(inputValues[input.name]) 
                                                                        ? [...inputValues[input.name]] 
                                                                        : [];
                                                                    
                                                                    if (selected) {
                                                                        handleInputChange(
                                                                            input.name, 
                                                                            currentValues.filter(v => v !== optionValue)
                                                                        );
                                                                    } else {
                                                                        handleInputChange(
                                                                            input.name,
                                                                            [...currentValues, optionValue]
                                                                        );
                                                                    }
                                                                }}
                                                                className={cn(
                                                                    "flex items-center gap-2 h-11 px-4 transition-all duration-300 rounded-xl border-2 transform hover:scale-105 active:scale-95",
                                                                    selected 
                                                                        ? "bg-gradient-to-r from-green-500 to-emerald-600 text-white border-green-500 hover:from-green-600 hover:to-emerald-700 shadow-lg" 
                                                                        : "bg-white/80 backdrop-blur-sm border-green-200 hover:border-green-300 hover:bg-green-50 text-gray-700"
                                                                )}
                                                            >
                                                                {optionIcon && getPlatformIcon(optionIcon, "h-4 w-4")}
                                                                {optionLabel}
                                                            </Button>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        
                                        {input.type === 'select_slider' && input.options && (
                                            <div className="space-y-2">
                                                <Slider
                                                    value={[inputValues[input.name] && input.options ? input.options.findIndex(opt => 
                                                        getOptionValue(opt) === inputValues[input.name]
                                                    ) : 0]}
                                                    defaultValue={input.default !== undefined ? [input.options.findIndex(opt => 
                                                        getOptionValue(opt) === input.default
                                                    )] : undefined}
                                                    onValueChange={(values) => {
                                                        const selectedIndex = values[0];
                                                        const selectedOption = input.options?.[selectedIndex];
                                                        if (selectedOption) {
                                                            handleInputChange(input.name, getOptionValue(selectedOption));
                                                        }
                                                    }}
                                                    options={input.options.map(opt => getOptionLabel(opt))}
                                                    showTooltip={true}
                                                    showSelectedValue={true}
                                                    className={cn(
                                                        "w-full",
                                                        inputErrors[input.name] ? 'border-red-500' : ''
                                                    )}
                                                />
                                            </div>
                                        )}
                                        
                                            {input.type === 'ideas' && (
                                                <div className="space-y-4">
                                                    <div className={cn(
                                                        "rounded-xl border-2 bg-white/80 backdrop-blur-sm transition-all duration-200 focus-within:shadow-md overflow-hidden",
                                                        input.required 
                                                            ? "border-purple-300/60 focus-within:border-purple-400 shadow-purple-100/20" 
                                                            : "border-gray-300/60 focus-within:border-gray-400"
                                                    )}>
                                                        <textarea
                                                            id={input.name}
                                                            value={inputValues[input.name] || ''}
                                                            placeholder={input.placeholder || "Enter your ideas here..."}
                                                            onChange={e => {
                                                                handleInputChange(input.name, e.target.value);
                                                                // Clear active suggestion state when user edits
                                                                if (convergenceData?.metadata?.ideas) {
                                                                    const ideas = convergenceData.metadata.ideas;
                                                                    const matches = Object.entries(ideas).find(([_, content]) => content === e.target.value);
                                                                    setActiveSuggestion(matches ? matches[0] : null);
                                                                }
                                                            }}
                                                            className={cn(
                                                                "w-full h-32 p-4 bg-transparent border-0 focus:outline-none focus:ring-0 text-base resize-none",
                                                                inputErrors[input.name] ? 'text-red-600' : ''
                                                            )}
                                                            rows={5}
                                                        />
                                                    
                                                        {selectedConvergence && convergenceData && (
                                                            <div className="border-t border-purple-100/50 p-4 bg-gradient-to-r from-purple-50/50 to-indigo-50/30">
                                                                <p className="text-sm font-medium text-purple-700 mb-3">Click to use a suggested idea from the selected convergence:</p>
                                                                <div className="flex flex-wrap gap-2">
                                                                    {/* Brand Opinion button */}
                                                                    <Button
                                                                        type="button"
                                                                        variant={activeSuggestion === 'brand_opinion' ? 'default' : 'ghost'}
                                                                        size="sm"
                                                                        onClick={() => {
                                                                            const brandOpinion = convergenceData?.metadata?.ideas?.brand_opinion;
                                                                            if (brandOpinion) {
                                                                                handleInputChange(input.name, brandOpinion);
                                                                                setActiveSuggestion('brand_opinion');
                                                                            }
                                                                        }}
                                                                        className={cn(
                                                                            "text-sm h-8 px-3 transition-all duration-300 rounded-lg transform hover:scale-105 active:scale-95",
                                                                            "hover:bg-purple-100 hover:text-purple-700",
                                                                            activeSuggestion === 'brand_opinion' 
                                                                                ? "bg-gradient-to-r from-purple-500 to-indigo-600 text-white hover:from-purple-600 hover:to-indigo-700 shadow-lg" 
                                                                                : "bg-white/80 border border-purple-200 hover:border-purple-300",
                                                                            !convergenceData?.metadata?.ideas?.brand_opinion && "opacity-50",
                                                                            "cursor-pointer"
                                                                        )}
                                                                        disabled={!convergenceData?.metadata?.ideas?.brand_opinion}
                                                                    >
                                                                        <span className="mr-1 text-xs opacity-70">+</span>
                                                                        Brand Opinion
                                                                    </Button>
                                                                
                                                                    {/* Hot Takes button */}
                                                                    <Button
                                                                        type="button"
                                                                        variant={activeSuggestion === 'hot_takes' ? 'default' : 'ghost'}
                                                                        size="sm"
                                                                        onClick={() => {
                                                                            const hotTakes = convergenceData?.metadata?.ideas?.hot_takes;
                                                                            if (hotTakes) {
                                                                                handleInputChange(input.name, hotTakes);
                                                                                setActiveSuggestion('hot_takes');
                                                                            }
                                                                        }}
                                                                        className={cn(
                                                                            "text-sm h-8 px-3 transition-all duration-300 rounded-lg transform hover:scale-105 active:scale-95",
                                                                            "hover:bg-purple-100 hover:text-purple-700",
                                                                            activeSuggestion === 'hot_takes' 
                                                                                ? "bg-gradient-to-r from-purple-500 to-indigo-600 text-white hover:from-purple-600 hover:to-indigo-700 shadow-lg" 
                                                                                : "bg-white/80 border border-purple-200 hover:border-purple-300",
                                                                            !convergenceData?.metadata?.ideas?.hot_takes && "opacity-50",
                                                                            "cursor-pointer"
                                                                        )}
                                                                        disabled={!convergenceData?.metadata?.ideas?.hot_takes}
                                                                    >
                                                                        <span className="mr-1 text-xs opacity-70">+</span>
                                                                        Hot Takes
                                                                    </Button>
                                                                
                                                                    {/* Controversial Takes button */}
                                                                    <Button
                                                                        type="button"
                                                                        variant={activeSuggestion === 'controversial_takes' ? 'default' : 'ghost'}
                                                                        size="sm"
                                                                        onClick={() => {
                                                                            const controversialTakes = convergenceData?.metadata?.ideas?.controversial_takes;
                                                                            if (controversialTakes) {
                                                                                handleInputChange(input.name, controversialTakes);
                                                                                setActiveSuggestion('controversial_takes');
                                                                            }
                                                                        }}
                                                                        className={cn(
                                                                            "text-sm h-8 px-3 transition-all duration-300 rounded-lg transform hover:scale-105 active:scale-95",
                                                                            "hover:bg-purple-100 hover:text-purple-700",
                                                                            activeSuggestion === 'controversial_takes' 
                                                                                ? "bg-gradient-to-r from-purple-500 to-indigo-600 text-white hover:from-purple-600 hover:to-indigo-700 shadow-lg" 
                                                                                : "bg-white/80 border border-purple-200 hover:border-purple-300",
                                                                            !convergenceData?.metadata?.ideas?.controversial_takes && "opacity-50",
                                                                            "cursor-pointer"
                                                                        )}
                                                                        disabled={!convergenceData?.metadata?.ideas?.controversial_takes}
                                                                    >
                                                                        <span className="mr-1 text-xs opacity-70">+</span>
                                                                        Controversial Takes
                                                                    </Button>
                                                                
                                                                    {/* Unique Insights button */}
                                                                    <Button
                                                                        type="button"
                                                                        variant={activeSuggestion === 'unique_insights' ? 'default' : 'ghost'}
                                                                        size="sm"
                                                                        onClick={() => {
                                                                            const uniqueInsights = convergenceData?.metadata?.ideas?.unique_insights;
                                                                            if (uniqueInsights) {
                                                                                handleInputChange(input.name, uniqueInsights);
                                                                                setActiveSuggestion('unique_insights');
                                                                            }
                                                                        }}
                                                                        className={cn(
                                                                            "text-sm h-8 px-3 transition-all duration-300 rounded-lg transform hover:scale-105 active:scale-95",
                                                                            "hover:bg-purple-100 hover:text-purple-700",
                                                                            activeSuggestion === 'unique_insights' 
                                                                                ? "bg-gradient-to-r from-purple-500 to-indigo-600 text-white hover:from-purple-600 hover:to-indigo-700 shadow-lg" 
                                                                                : "bg-white/80 border border-purple-200 hover:border-purple-300",
                                                                            !convergenceData?.metadata?.ideas?.unique_insights && "opacity-50",
                                                                            "cursor-pointer"
                                                                        )}
                                                                        disabled={!convergenceData?.metadata?.ideas?.unique_insights}
                                                                    >
                                                                        <span className="mr-1 text-xs opacity-70">+</span>
                                                                        Unique Insights
                                                                    </Button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                                
                                                {!convergenceData && selectedConvergence && (
                                                    <p className="text-sm text-muted-foreground italic">No idea data available for this convergence.</p>
                                                )}
                                                {!selectedConvergence && (
                                                    <p className="text-sm text-muted-foreground italic">Select a convergence above to see suggested ideas.</p>
                                                )}
                                            </div>
                                        )}
                                        
                                        {input.type === 'url' && (
                                            <div className="space-y-2">
                                                <div className={cn(
                                                    "rounded-lg border border-input bg-background",
                                                    input.required && "border-l-4 border-l-blue-300 dark:border-l-blue-700"
                                                )}>
                                                    <Input
                                                        id={input.name}
                                                        type="url"
                                                        value={inputValues[input.name] || ''}
                                                        placeholder={input.placeholder || "https://example.com"}
                                                        onChange={e => {
                                                            const url = e.target.value;
                                                            // Auto-prefix with https:// if no protocol is specified
                                                            if (url && !url.match(/^https?:\/\//)) {
                                                                const normalizedUrl = `https://${url}`;
                                                                // Only auto-prefix if it looks like it could be a domain
                                                                if (url.includes('.') || url.includes('localhost')) {
                                                                    handleInputChange(input.name, normalizedUrl);
                                                                    return;
                                                                }
                                                            }
                                                            handleInputChange(input.name, url);
                                                        }}
                                                        onBlur={e => {
                                                            const url = e.target.value;
                                                            if (!url) {
                                                                delete inputErrors[input.name];
                                                                setInputErrors({...inputErrors});
                                                                return;
                                                            }
                                                            
                                                            // Validate URL format
                                                            try {
                                                                new URL(url);
                                                                delete inputErrors[input.name];
                                                                setInputErrors({...inputErrors});
                                                            } catch {
                                                                setInputErrors({
                                                                    ...inputErrors,
                                                                    [input.name]: 'Please enter a valid URL'
                                                                });
                                                            }
                                                        }}
                                                        className={cn(
                                                            "border-0 focus-visible:ring-0 focus-visible:ring-offset-0",
                                                            inputErrors[input.name] ? 'border-red-500' : ''
                                                        )}
                                                    />
                                                </div>
                                                {inputErrors[input.name] && (
                                                    <p className="text-sm text-red-500">{inputErrors[input.name]}</p>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                        </div>
                    )}
                    
                    {/* Media Generation Section - Only show for blog_post and social_post */}
                    {(contentType.id === 'blog_post' || contentType.id === 'social_post') && (() => {
                        // Get supported media types for the selected post type
                        const postType = inputValues.platform_post_type as PlatformPostType;
                        const supportedMediaTypes = postType && POST_TYPE_LABELS[postType]?.supportedMediaTypes || ['image', 'video'];
                        const supportsImage = supportedMediaTypes.includes('image');
                        const supportsVideo = supportedMediaTypes.includes('video');
                        const supportsNoMedia = supportedMediaTypes.includes('none');

                        // If post type only supports 'none', don't show media generation section at all
                        if (supportsNoMedia && supportedMediaTypes.length === 1) {
                            return null;
                        }

                        return (
                            <div className="relative mb-8">
                                <div className="bg-white/60 backdrop-blur-sm rounded-2xl border border-purple-100/50 shadow-sm p-6 transition-all duration-300 hover:shadow-md">
                                    <div className="flex items-center gap-3 mb-6">
                                        <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center shadow-lg">
                                            <Image className="h-4 w-4 text-white" />
                                        </div>
                                        <h3 className="text-xl font-semibold bg-gradient-to-r from-purple-700 to-pink-700 bg-clip-text text-transparent">
                                            Media Automation
                                        </h3>
                                    </div>

                                    <div className="space-y-4">
                                        <div className="space-y-3 p-5 rounded-xl transition-all duration-300 hover:shadow-sm border-2 bg-gradient-to-r from-purple-50/50 via-white/40 to-pink-50/30 border-purple-200/50">
                                            <div className="flex items-center gap-3 mb-4">
                                                <Label className="text-base font-semibold text-gray-800">
                                                    Would you like to automatically generate media for this content?
                                                </Label>
                                            </div>

                                            <div className="flex flex-wrap gap-3">
                                                {supportsImage && (
                                                    <Button
                                                        type="button"
                                                        variant={inputValues.mediaGeneration === 'images' ? "default" : "outline"}
                                                        onClick={() => handleInputChange('mediaGeneration', 'images')}
                                                        className={cn(
                                                            "flex items-center gap-2 h-11 px-4 transition-all duration-300 rounded-xl border-2 transform hover:scale-105 active:scale-95",
                                                            inputValues.mediaGeneration === 'images'
                                                                ? "bg-gradient-to-r from-purple-500 to-pink-600 text-white border-purple-500 hover:from-purple-600 hover:to-pink-700 shadow-lg"
                                                                : "bg-white/80 backdrop-blur-sm border-purple-200 hover:border-purple-300 hover:bg-purple-50 text-gray-700"
                                                        )}
                                                    >
                                                        <Image className="h-4 w-4" />
                                                        Generate Image(s)
                                                    </Button>
                                                )}

                                                {supportsVideo && (
                                                    <Button
                                                        disabled
                                                        type="button"
                                                        variant={inputValues.mediaGeneration === 'videos' ? "default" : "outline"}
                                                        onClick={() => handleInputChange('mediaGeneration', 'videos')}
                                                        className={cn(
                                                            "flex items-center gap-2 h-11 px-4 transition-all duration-300 rounded-xl border-2 transform hover:scale-105 active:scale-95",
                                                            inputValues.mediaGeneration === 'videos'
                                                                ? "bg-gradient-to-r from-purple-500 to-pink-600 text-white border-purple-500 hover:from-purple-600 hover:to-pink-700 shadow-lg"
                                                                : "bg-white/80 backdrop-blur-sm border-purple-200 hover:border-purple-300 hover:bg-purple-50 text-gray-700"
                                                        )}
                                                    >
                                                        <Video className="h-4 w-4" />
                                                        Generate Video(s)
                                                    </Button>
                                                )}

                                                <Button
                                                    type="button"
                                                    variant={inputValues.mediaGeneration === 'none' ? "default" : "outline"}
                                                    onClick={() => handleInputChange('mediaGeneration', 'none')}
                                                    className={cn(
                                                        "flex items-center gap-2 h-11 px-4 transition-all duration-300 rounded-xl border-2 transform hover:scale-105 active:scale-95",
                                                        inputValues.mediaGeneration === 'none'
                                                            ? "bg-gradient-to-r from-gray-500 to-slate-600 text-white border-gray-500 hover:from-gray-600 hover:to-slate-700 shadow-lg"
                                                            : "bg-white/80 backdrop-blur-sm border-gray-200 hover:border-gray-300 hover:bg-gray-50 text-gray-700"
                                                    )}
                                                >
                                                    <X className="h-4 w-4" />
                                                    No Media Generation
                                                </Button>
                                            </div>

                                            <p className="text-sm text-gray-600 mt-3">
                                                {contentType.id === 'blog_post'
                                                    ? "Generate relevant images or videos to enhance your blog post and make it more engaging."
                                                    : postType && POST_TYPE_LABELS[postType]
                                                    ? `For ${POST_TYPE_LABELS[postType].label}, you can generate ${supportedMediaTypes.filter(t => t !== 'none').join(' or ')} content.`
                                                    : "Generate media assets optimized for your social media platform to increase engagement."
                                                }
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })()}
                    
                    {/* Separator */}
                    {hasDependencies && (hasRequiredInputs || (contentType.id === 'blog_post' || contentType.id === 'social_post')) && <Separator className="my-4" />}
                    
                    {/* Dependencies Section */}
                    {hasDependencies && (
                        <div className="relative mb-8">
                            <div className="bg-white/60 backdrop-blur-sm rounded-2xl border border-orange-100/50 shadow-sm p-6 transition-all duration-300 hover:shadow-md">
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl flex items-center justify-center shadow-lg">
                                        <ArrowUpDown className="h-4 w-4 text-white" />
                                    </div>
                                    <h3 className="text-xl font-semibold bg-gradient-to-r from-orange-700 to-red-700 bg-clip-text text-transparent">
                                        Content Dependencies
                                    </h3>
                                </div>
                            
                                {isLoadingDependencies ? (
                                    <div className="flex flex-col items-center p-8 space-y-6">
                                        <div className="relative">
                                            <div className="w-12 h-12 rounded-full border-4 border-orange-200/30" />
                                            <div className="absolute inset-0 w-12 h-12 rounded-full border-4 border-t-orange-500 border-r-red-600 border-b-transparent border-l-transparent animate-spin" />
                                        </div>
                                        <div className="text-center space-y-2">
                                            {shownMessages.map((message, index) => (
                                                <div 
                                                    key={index} 
                                                    className="text-sm text-orange-600 font-medium animate-fade-in transition-all duration-500"
                                                    style={{animationDelay: `${index * 200}ms`}}
                                                >
                                                    {message}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                            ) : dependencyError ? (
                                <Alert variant="destructive">
                                    <AlertCircle className="h-4 w-4" />
                                    <AlertDescription>{dependencyError}</AlertDescription>
                                </Alert>
                            ) : missingDependencyTypes.length > 0 ? (
                                <Alert variant="destructive">
                                    <AlertCircle className="h-4 w-4" />
                                    <AlertDescription>
                                        This campaign is missing required content of type: {missingDependencyTypes.join(', ')}
                                    </AlertDescription>
                                </Alert>
                            ) : (
                                <div className="space-y-4">
                                    {contentType.dependencies.map((dep) => (
                                        <div key={dep.type} className="space-y-2">
                                            <Label>
                                                {dep.description}
                                                {dep.required && <span className="text-red-500">*</span>}
                                            </Label>
                                            <Select
                                                value={selectedDependencies[dep.type]}
                                                onValueChange={(value) => handleDependencySelect(dep.type, value)}
                                            >
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Select content..." />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {availableDependencies[dep.type]?.map((content) => (
                                                        <SelectItem key={content.id} value={content.id}>
                                                            {content.name}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    ))}
                                </div>
                            )}
                            </div>
                        </div>
                    )}
                </div>

                <div className="bg-gradient-to-r from-slate-50 to-blue-50/30 border-t border-blue-100/50 p-8">
                    <div className="flex justify-end gap-4">
                        <Button 
                            variant="outline" 
                            onClick={onClose}
                            className="h-12 px-6 rounded-xl border-2 border-gray-300 hover:border-gray-400 bg-white/80 backdrop-blur-sm transition-all duration-200 hover:shadow-sm"
                        >
                            Cancel
                        </Button>
                        
                        <Button 
                            onClick={handleComplete}
                            disabled={
                                !isCampaignStepValid || 
                                (hasRequiredInputs && !isInputStepValid()) || 
                                (hasDependencies && (!isDependencyStepValid() || missingDependencyTypes.length > 0))
                            }
                            className={cn(
                                "h-12 px-8 rounded-xl transition-all duration-300 transform hover:scale-105 active:scale-95 shadow-lg",
                                (!isCampaignStepValid || (hasRequiredInputs && !isInputStepValid()) || (hasDependencies && (!isDependencyStepValid() || missingDependencyTypes.length > 0)))
                                    ? "bg-gray-300 text-gray-500 cursor-not-allowed hover:scale-100"
                                    : "bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white font-semibold"
                            )}
                        >
                            Create Content
                        </Button>
                    </div>
                </div>
                </>
            </DialogContent>
        </Dialog>
    )
}